import React, { useContext, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Breadcrumb from "./components/Breadcrumb";
import YouTubePopup from "./components/YouTubePopup";
import { UserContext } from "./contexts/UserContext";
import { triggerGaEvents } from './common/gtag';

const decodePath = (segment) => decodeURIComponent(segment.replace(/~/g, "/"));

const encodePath = (label) => label.replace(/\//g, "~");

const getMenuLevel = (menu, segments) => {
  let level = menu;
  for (const seg of segments) {
    const label = decodePath(seg);
    const found = level.find((item) => item.label === label);
    if (found && found.menu) {
      level = found.menu;
    } else {
      level = [];
      break;
    }
  }
  return level;
};

const MenuView = ({ menu }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const { user } = useContext(UserContext);

  const pathSegments = useMemo(() =>
    location.pathname
      .split("/")
      .filter(Boolean)
      .map(decodePath),
    [location.pathname]
  );

  const currentMenu = useMemo(() => getMenuLevel(menu, pathSegments), [menu, pathSegments]);

  const handleNavigate = (label) => {
    const encoded = encodePath(label);
    const newPath = [...pathSegments, encoded].map(encodeURIComponent).join("/");
    navigate(`/${newPath}`);
  };

  const openVideoPopup = (videoUrl) => {
    if (videoUrl) {
      setVideoUrl(videoUrl);
      setIsPopupOpen(true);
    }
  };

  const closePopup = () => {
    setIsPopupOpen(false);
  };

  return (
    <div className="mobile:px-0 bg-[#F9F9FB] h-full">
      <div className="mb-4 flex items-center gap-2 text-sm font-medium text-gray-600 bg-[#EFEFF4] px-6 py-4 overflow-auto">
        <Link to="/" className="hover:text-blue-500 hover:underline">
          Dashboard
        </Link>
        <Breadcrumb pathSegments={pathSegments} />
      </div>

      {currentMenu.length === 0 ?
        (
          <p className="text-red-500">No sub-menu found.</p>
        )
        :
        (
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mobile:gap-3 px-6">
            {currentMenu.map((item, i) => {
              const hasMenu = item.menu && item.menu.length > 0;

              return (
                <li key={i}
                  className={`flex flex-col items-baseline justify-between min-h-[140px] p-4 rounded-2xl border border-[#D7D7DC] hover:shadow-[4px_4px_13px_0px_#00000029] ${hasMenu && !item?.disable ? "cursor-pointer" : ""} ${item.disable ? "bg-[#EFEFF4] " : "bg-white"}`}
                  onClick={() => { if (hasMenu && !item?.disable) { handleNavigate(item.label) } }}
                >

                  <div className="flex justify-between w-full">

                    {item.icon && (
                      <div className={`w-[42px] h-[42px] mb-[10px] p-[5px] flex justify-center items-center rounded-[4px] text-2xl ${item.bgColor || "bg-white"}`}>
                        <img src={item.icon} alt={`${module.title} icon`} />
                      </div>
                    )}

                    {(
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openVideoPopup(item?.videoUrl)
                        }}
                        className={`${item.icon && item.url() ? 'ml-[11px] mr-auto' : ''} w-[34px] h-[34px] flex items-center justify-center text-gray-800 dark:text-white bg-[#EFEFF4] focus:ring-4 focus:ring-gray-300 font-medium rounded-lg text-sm lg:py-2.5 mr-2 dark:hover:bg-gray-700 focus:outline-none dark:focus:ring-gray-800 ${item.videoUrl ? "cursor-pointer" : "cursor-not-allowed text-[#3C3C3C]"}`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="rgba(60,60,60,1)">
                          <path d="M3 3.9934C3 3.44476 3.44495 3 3.9934 3H20.0066C20.5552 3 21 3.44495 21 3.9934V20.0066C21 20.5552 20.5551 21 20.0066 21H3.9934C3.44476 21 3 20.5551 3 20.0066V3.9934ZM5 5V19H19V5H5ZM10.6219 8.41459L15.5008 11.6672C15.6846 11.7897 15.7343 12.0381 15.6117 12.2219C15.5824 12.2658 15.5447 12.3035 15.5008 12.3328L10.6219 15.5854C10.4381 15.708 10.1897 15.6583 10.0672 15.4745C10.0234 15.4088 10 15.3316 10 15.2526V8.74741C10 8.52649 10.1791 8.34741 10.4 8.34741C10.479 8.34741 10.5562 8.37078 10.6219 8.41459Z" />
                        </svg>
                      </button>
                    )}


                    {item.url() && !item.disable && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const url = item.url();
                          if (url && !item?.disable) {
                            triggerGaEvents('menu_card_click', { event_category: 'Navigation', event_label: item.label, item_url: url });
                            window.open(url, '_blank');
                          }
                        }}

                        className={`flex items-center max-h-[35px] px-[10px] py-[8px] bg-red-50 rounded-md hover:bg-red-100 ${item.disable ? "cursor-not-allowed text-[#3C3C3C]" : "text-red-600"}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill={`${item.disable ? "" : "rgba(217,35,45,1)"}`}>
                          <path d="M10 6V8H5V19H16V14H18V20C18 20.5523 17.5523 21 17 21H4C3.44772 21 3 20.5523 3 20V7C3 6.44772 3.44772 6 4 6H10ZM21 3V11H19L18.9999 6.413L11.2071 14.2071L9.79289 12.7929L17.5849 5H13V3H21Z"></path>
                        </svg>
                        <span className="text-[14px] leading-5 font-semibold ml-[5px]">Open</span>
                      </button>
                    )}

                  </div>

                  <div className="flex justify-between items-end w-full">
                    <div>
                      <h3 className={`font-bold text-base whitespace-pre-line ${item.isLoked ? "text-[#808080]" : "text-[#3C3C3C]"}`}>
                        {item.label}
                      </h3>
                      {item.tasks && (<p className="text-sm text-gray-500 mt-2">{item.tasks}</p>)}
                    </div>

                    {hasMenu && <span className="inline-block  text-xs font-medium  px-2 py-1 rounded">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="rgba(60,60,60,1)">
                        <path d="M13.1717 12.0007L8.22192 7.05093L9.63614 5.63672L16.0001 12.0007L9.63614 18.3646L8.22192 16.9504L13.1717 12.0007Z"></path>
                      </svg>
                    </span>}

                  </div>
                </li>
              );
            })}
          </ul>
        )}

      <YouTubePopup
        videoUrl={videoUrl}
        isOpen={isPopupOpen}
        closePopup={closePopup}
      />
    </div>
  );
};

export default MenuView;
